import { useRef, useState } from "react";
import axios from "axios";
import { CheckCircle2, AlertCircle, Loader2, ArrowUpRight } from "lucide-react";
import { SERVICES } from "@/data/services";
import { BUDGETS, COMPANY } from "@/data/site";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "";
const SERVICE_OPTIONS = [...SERVICES.map((s) => s.title), "Not sure yet / Multiple services"];
const EMPTY = { name: "", company: "", email: "", phone: "", service: "", budget: "", deadline: "", description: "", website: "" };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_RE = /^[0-9+()\s-]{7,20}$/;
const ORDER = ["name", "email", "phone", "service", "description"];

const validate = (v) => {
  const e = {};
  if (v.name.trim().length < 2) e.name = "Please enter your full name.";
  if (!v.email.trim()) e.email = "Please enter your email address.";
  else if (!EMAIL_RE.test(v.email.trim())) e.email = "Please enter a valid email address, e.g. name@company.co.uk.";
  if (v.phone.trim() && !PHONE_RE.test(v.phone.trim())) e.phone = "Please enter a valid phone number.";
  if (!v.service) e.service = "Please choose the service you need.";
  if (v.description.trim().length < 20) e.description = "Please describe your project in at least 20 characters.";
  return e;
};

const fieldCls = (err) =>
  `mt-2 w-full rounded-none border-0 border-b-2 bg-transparent px-0 py-3 text-base text-navy placeholder:text-slatey/80 transition-colors duration-300 focus:border-royal focus:outline-none focus:ring-0 focus-visible:outline-none ${
    err ? "border-red-600" : "border-navy/20 hover:border-navy/50"
  }`;

const Field = ({ id, label, required, error, children, className = "" }) => (
  <div className={className}>
    <label htmlFor={id} className="text-sm font-semibold text-navy">
      {label} {required ? <span className="text-royal" aria-hidden="true">*</span> : <span className="font-normal text-slatey">(optional)</span>}
    </label>
    {children}
    {error && <p id={`${id}-error`} className="mt-2 flex items-center gap-1.5 text-sm text-red-700" data-testid={`enquiry-${id}-error`}><AlertCircle className="h-4 w-4" aria-hidden="true" />{error}</p>}
  </div>
);

export const EnquiryForm = () => {
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const formRef = useRef(null);

  const set = (k) => (e) => {
    setValues((v) => ({ ...v, [k]: e.target.value }));
    if (errors[k]) setErrors((x) => ({ ...x, [k]: undefined }));
  };
  const aria = (k) => ({ "aria-invalid": !!errors[k], "aria-describedby": errors[k] ? `${k}-error` : undefined });

  const focusFirst = (errs) => {
    const first = ORDER.find((k) => errs[k]);
    if (first) formRef.current?.querySelector(`#${first}`)?.focus();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const errs = validate(values);
    setErrors(errs);
    if (Object.keys(errs).length) {
      setStatus("invalid");
      setMessage("Please correct the highlighted fields and try again.");
      focusFirst(errs);
      return;
    }
    if (!API) {
      setStatus("error");
      setMessage(`The enquiry form is not connected right now. Please email ${COMPANY.email} directly.`);
      return;
    }
    setStatus("submitting");
    try {
      await axios.post(`${API}/enquiries`, values, { timeout: 20000 });
      setStatus("success");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (Array.isArray(detail)) {
        const serverErrs = {};
        detail.forEach((d) => { const k = d?.loc?.[d.loc.length - 1]; if (k) serverErrs[k] = String(d?.msg || "Invalid value").replace(/^Value error, /, ""); });
        setErrors(serverErrs);
        focusFirst(serverErrs);
        setMessage("Some details need attention. Please check the highlighted fields.");
      } else {
        setMessage(typeof detail === "string" ? detail : `We couldn’t send your enquiry. Please try again or email ${COMPANY.email}.`);
      }
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div role="status" aria-live="polite" className="rounded-lg bg-navy p-8 text-white sm:p-12" data-testid="enquiry-success">
        <CheckCircle2 className="h-12 w-12 text-lime" aria-hidden="true" />
        <h2 className="mt-6 font-display text-3xl font-bold tracking-tight sm:text-4xl">Thank you, {values.name.split(" ")[0]}.</h2>
        <p className="mt-4 max-w-md leading-relaxed text-white/85">Your enquiry has been received. We’ll review your project details and reply to {values.email} as soon as possible.</p>
        <button type="button" data-testid="enquiry-send-another-button" onClick={() => { setValues(EMPTY); setStatus("idle"); setErrors({}); }} className="mt-8 rounded-full border border-white/30 px-6 py-3 text-sm font-semibold transition-colors hover:border-lime hover:bg-lime hover:text-navy">
          Send another enquiry
        </button>
      </div>
    );
  }

  return (
    <form ref={formRef} onSubmit={onSubmit} noValidate className="grid gap-x-8 gap-y-8 sm:grid-cols-2" data-testid="enquiry-form" aria-label="Project enquiry form">
      <Field id="name" label="Full Name" required error={errors.name}>
        <input id="name" name="name" autoComplete="name" value={values.name} onChange={set("name")} className={fieldCls(errors.name)} placeholder="Your full name" data-testid="enquiry-name-input" {...aria("name")} />
      </Field>
      <Field id="company" label="Business or Company Name" error={errors.company}>
        <input id="company" name="company" autoComplete="organization" value={values.company} onChange={set("company")} className={fieldCls(errors.company)} placeholder="Company name" data-testid="enquiry-company-input" />
      </Field>
      <Field id="email" label="Email Address" required error={errors.email}>
        <input id="email" name="email" type="email" autoComplete="email" value={values.email} onChange={set("email")} className={fieldCls(errors.email)} placeholder="name@company.co.uk" data-testid="enquiry-email-input" {...aria("email")} />
      </Field>
      <Field id="phone" label="Phone Number" error={errors.phone}>
        <input id="phone" name="phone" type="tel" autoComplete="tel" value={values.phone} onChange={set("phone")} className={fieldCls(errors.phone)} placeholder="07…" data-testid="enquiry-phone-input" {...aria("phone")} />
      </Field>
      <Field id="service" label="Service Required" required error={errors.service}>
        <select id="service" name="service" value={values.service} onChange={set("service")} className={fieldCls(errors.service)} data-testid="enquiry-service-select" {...aria("service")}>
          <option value="">Select a service</option>
          {SERVICE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </Field>
      <Field id="budget" label="Estimated Budget" error={errors.budget}>
        <select id="budget" name="budget" value={values.budget} onChange={set("budget")} className={fieldCls(errors.budget)} data-testid="enquiry-budget-select">
          <option value="">Select a range</option>
          {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
      </Field>
      <Field id="deadline" label="Project Deadline" error={errors.deadline}>
        <input id="deadline" name="deadline" type="date" value={values.deadline} onChange={set("deadline")} className={fieldCls(errors.deadline)} data-testid="enquiry-deadline-input" />
      </Field>
      <div className="hidden" aria-hidden="true">
        <label htmlFor="website">Website</label>
        <input id="website" name="website" tabIndex={-1} autoComplete="off" value={values.website} onChange={set("website")} />
      </div>
      <Field id="description" label="Project Description" required error={errors.description} className="sm:col-span-2">
        <textarea id="description" name="description" rows={5} value={values.description} onChange={set("description")} className={`${fieldCls(errors.description)} resize-y`} placeholder="Tell us about your business, your goals and what you’d like designed." data-testid="enquiry-description-input" {...aria("description")} />
      </Field>

      <div className="sm:col-span-2">
        {(status === "invalid" || status === "error") && (
          <p role="alert" className="mb-6 flex items-start gap-2 rounded-md bg-red-50 p-4 text-sm text-red-800" data-testid="enquiry-form-error">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />{message}
          </p>
        )}
        <button
          type="submit"
          disabled={status === "submitting"}
          data-testid="enquiry-submit-button"
          className="group inline-flex items-center gap-3 rounded-full bg-royal py-4 pl-7 pr-4 text-base font-semibold text-white transition-[background-color,color] duration-300 hover:bg-lime hover:text-navy disabled:cursor-wait disabled:opacity-70"
        >
          {status === "submitting" ? "Sending…" : "Send Enquiry"}
          {status === "submitting" ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> : <ArrowUpRight className="h-5 w-5 transition-transform duration-500 ease-expo group-hover:rotate-45" aria-hidden="true" />}
        </button>
        <p className="mt-4 text-xs text-slatey">Fields marked * are required. Your details are only used to respond to your enquiry — see our Privacy Policy.</p>
      </div>
    </form>
  );
};
